import AgoraRTC, {
  IAgoraRTCClient,
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
} from 'agora-rtc-sdk-ng';
import {ViewingSessionRequest} from './useRTCClient';
import {useCallback, useEffect, useRef, useState} from 'react';

import {EnhancedConnectionState} from '../types/EnhancedConnectionState';

export const useMediaTracks = ({
  rtcClient,
  isInCall,
  setIsInCall,
  tourId,
  viewingSessionRequest,
  isHost,
  toggleSync,
  connectionState,
}: {
  rtcClient: IAgoraRTCClient | null;
  isInCall: boolean;
  setIsInCall: (value: boolean) => void;
  tourId: string;
  viewingSessionRequest: ViewingSessionRequest;
  isHost: boolean;
  toggleSync: () => void;
  connectionState: EnhancedConnectionState;
}) => {
  const [isMicActive, setIsMicActive] = useState(true);
  const [isVideoActive, setIsVideoActive] = useState(true);
  const [mediaPermissions, setMediaPermissions] = useState({
    video: true,
    audio: true,
  });
  const [isStartingCall, setIsStartingCall] = useState(false);

  const localTracks = useRef<{
    audioTrack: IMicrophoneAudioTrack | null;
    videoTrack: ICameraVideoTrack | null;
  }>({
    audioTrack: null,
    videoTrack: null,
  });

  const checkMediaPermissions = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({audio: true, video: true});
      setMediaPermissions({audio: true, video: true});
      return true;
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        // Permission denied
        setMediaPermissions({audio: false, video: false});
        return false;
      }

      try {
        await navigator.mediaDevices.getUserMedia({audio: true});
        setMediaPermissions({audio: true, video: false});
        return true;
      } catch (audioErr) {
        setMediaPermissions({audio: false, video: false});
        return false;
      }
    }
  };

  const startCall = async () => {
    setIsStartingCall(true);

    try {
      if (!rtcClient) {
        throw new Error('RTC client not initialized');
      }

      const hasPermissions = await checkMediaPermissions();
      if (!hasPermissions) {
        setIsStartingCall(false);
        return;
      }

      await rtcClient.join(
        viewingSessionRequest.appId,
        tourId,
        viewingSessionRequest.rtcToken,
        viewingSessionRequest.viewingSessionUserId
      );

      let audioTrack: IMicrophoneAudioTrack | null = null;
      let videoTrack: ICameraVideoTrack | null = null;

      try {
        if (mediaPermissions.audio && mediaPermissions.video) {
          [
            audioTrack,
            videoTrack,
          ] = await AgoraRTC.createMicrophoneAndCameraTracks(
            {
              encoderConfig: 'speech_standard',
              AEC: true,
              AGC: true,
              ANS: true,
            },
            {
              encoderConfig: '480p',
              facingMode: 'user',
            }
          );
        } else if (mediaPermissions.audio) {
          audioTrack = await AgoraRTC.createMicrophoneAudioTrack({
            AEC: true,
            AGC: true,
            ANS: true,
          });
        } else if (mediaPermissions.video) {
          videoTrack = await AgoraRTC.createCameraVideoTrack({
            encoderConfig: '480p',
            facingMode: 'user',
          });
        }
      } catch (err) {
        console.error('Failed to create media tracks:', err);
      }

      localTracks.current = {
        audioTrack,
        videoTrack,
      };

      const tracksToPublish = [audioTrack, videoTrack].filter(
        (track) => track !== null
      ) as (IMicrophoneAudioTrack | ICameraVideoTrack)[];

      if (tracksToPublish.length > 0) {
        try {
          await rtcClient.publish(tracksToPublish);
          console.log('Published local tracks:', tracksToPublish.length);
        } catch (pubErr) {
          console.error('Error publishing tracks:', pubErr);
        }
      }

      if (videoTrack) {
        setTimeout(() => {
          try {
            const container = document.getElementById('local-video');
            if (container) {
              videoTrack?.play('local-video', {fit: 'cover'});
              console.log('Local video track playing');
            } else {
              console.error('Local video container not found');
            }
          } catch (err) {
            console.error('Error playing local video track:', err);
          }
        }, 200);
      }

      setIsMicActive(!!audioTrack);
      setIsVideoActive(!!videoTrack);
      setIsInCall(true);

      if (!isHost) {
        await toggleSync();
      }
    } catch (error) {
      console.error('Error starting call:', error);
    } finally {
      setIsStartingCall(false);
    }
  };

  const leaveCall = useCallback(async () => {
    try {
      if (localTracks.current.audioTrack) {
        localTracks.current.audioTrack.close();
      }

      if (localTracks.current.videoTrack) {
        localTracks.current.videoTrack.close();
      }

      localTracks.current = {audioTrack: null, videoTrack: null};

      if (rtcClient) {
        await rtcClient.leave();
      }

      setIsInCall(false);
    } catch (error) {
      console.error('Error leaving call:', error);
      // Force UI to reflect disconnected state even if there was an error
      setIsInCall(false);
    }
  }, [rtcClient, setIsInCall]);

  useEffect(() => {
    // If connection state becomes DISCONNECTED and we're in a call,
    // clean up local resources
    if (connectionState === 'DISCONNECTED' && isInCall) {
      if (localTracks.current.audioTrack) {
        localTracks.current.audioTrack.close();
      }

      if (localTracks.current.videoTrack) {
        localTracks.current.videoTrack.close();
      }

      localTracks.current = {audioTrack: null, videoTrack: null};
      setIsInCall(false);
    }
  }, [connectionState, isInCall, setIsInCall]);

  useEffect(() => {
    return () => {
      leaveCall();
    };
  }, [leaveCall]);

  const toggleMic = useCallback(() => {
    if (localTracks.current.audioTrack) {
      localTracks.current.audioTrack.setEnabled(!isMicActive);
      setIsMicActive(!isMicActive);
    }
  }, [isMicActive]);

  const toggleVideo = useCallback(() => {
    if (localTracks.current.videoTrack) {
      localTracks.current.videoTrack.setEnabled(!isVideoActive);
      setIsVideoActive(!isVideoActive);
    }
  }, [isVideoActive]);

  return {
    localTracks: localTracks.current,
    isMicActive,
    isVideoActive,
    mediaPermissions,
    isStartingCall,
    startCall,
    leaveCall,
    toggleMic,
    toggleVideo,
  };
};
